import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const searchFilings = createAsyncThunk(
  "s1PathologySearch/searchFilings",
  async ({ searchParams, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/s1_pathology/api/search_filings`,
        { ...searchParams, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) dispatch(logout());
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchFilingDetail = createAsyncThunk(
  "s1PathologySearch/fetchFilingDetail",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/s1_pathology/api/fetch_blood_filing`,
        { lab_report_id, liverun, license_key },
      );
      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) dispatch(logout());
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

const mapFiling = (filing, idx) => ({
  id: filing._id?.$oid || filing.id || idx,
  lab_report_id: filing.patient_data?.lab_report_id || filing.lab_report_id,
  emis_id: filing.patient_data?.emis_id || filing.emis_id,
  category: Array.isArray(filing.category_names)
    ? filing.category_names.join(", ")
    : filing.category_names || "",
  category_type: filing.patient_data?.is_normal ?? true,
  status: filing.result || "Unknown",
  starttime: filing.start_time?.$date
    ? new Date(filing.start_time.$date).toLocaleString()
    : filing.start_time || "",
  notes: Array.isArray(filing.actions)
    ? filing.actions.map((a) => a.msg || "").join(" | ")
    : "",
});

const pathologyFrameSlice = createSlice({
  name: "s1PathologySearch",
  initialState: {
    searchParams: {
      nhs_number: "",
      pathology_category: "",
      subtest_name: "",
      start_filing_date: "",
      end_filing_date: "",
      liverun: true,
    },
    searchResults: [],
    totalResults: 0,
    selectedFiling: null,
    selectedLabReportId: null,
    loadingSearch: false,
    loadingDetail: false,
    searchError: null,
    detailError: null,
  },
  reducers: {
    updateSearchParam(state, action) {
      const { field, value } = action.payload;
      state.searchParams[field] = value;
    },
    updateSearchParams(state, action) {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    clearSearchParams(state) {
      state.searchParams = {
        nhs_number: "",
        pathology_category: "",
        subtest_name: "",
        subtest_min: "",
        subtest_max: "",
        keyword: "",
        medication: "",
        problem_keyword: "",
        age_min: "",
        age_max: "",
        gender: "",
        message_keyword: "",
        liverun: state.searchParams.liverun,
      };
      state.searchResults = [];
      state.totalResults = 0;
      state.searchError = null;
    },
    clearSearchResults(state) {
      state.searchResults = [];
      state.totalResults = 0;
    },
    setSelectedLabReportId(state, action) {
      state.selectedLabReportId = action.payload;
    },
    clearFilingDetail(state) {
      state.selectedFiling = null;
      state.selectedLabReportId = null;
      state.detailError = null;
    },
    toggleLiverun(state) {
      state.searchParams.liverun = !state.searchParams.liverun;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchFilings.pending, (state) => {
        state.loadingSearch = true;
        state.searchError = null;
      })
      .addCase(searchFilings.fulfilled, (state, action) => {
        state.loadingSearch = false;
        if (Array.isArray(action.payload)) {
          state.searchResults = action.payload.map(mapFiling);
          state.totalResults = action.payload.length;
        } else if (action.payload.results) {
          state.searchResults = action.payload.results.map(mapFiling);
          state.totalResults =
            action.payload.total || action.payload.results.length;
        }
      })
      .addCase(searchFilings.rejected, (state, action) => {
        state.loadingSearch = false;
        state.searchError = action.error.message || "Failed to search filings";
      })
      .addCase(fetchFilingDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchFilingDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.selectedFiling = action.payload;
      })
      .addCase(fetchFilingDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError =
          action.error.message || "Failed to fetch filing detail";
      });
  },
});

export const {
  updateSearchParam,
  updateSearchParams,
  clearSearchParams,
  clearSearchResults,
  setSelectedLabReportId,
  clearFilingDetail,
  toggleLiverun,
} = pathologyFrameSlice.actions;

export default pathologyFrameSlice.reducer;
